import { defineStore } from 'pinia';
import {
  Employee,
  ResetPinResponsePayload,
} from '@/lib/request/types/employee';
import { Md5 } from 'ts-md5';
import { useEmployeeStore } from '@/stores/employees';
import { getKioskInstallationId } from '@/lib/util/auth';
import { reloadAnalyticsService } from '@/lib/util/analytics/reloadAnalyticsService';
import { resetAnalyticsService } from '@/lib/util/analytics/resetAnalyticsService';
import {
  resetPinResponseErrorMessage,
  resetPinResponseEmailErrorMessage,
  resetPinResponseMobileErrorMessage,
  resetPin,
  ResetPinMode,
} from '@/lib/request/resetPin';
import { getTranslations } from '@/lib/util/i18n';
import router from '@/router';
import { useAnalyticsService } from '@/lib/util/analytics';

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'user-store',
});

export interface UserStoreData {
  activeUser: Employee | null;
  resetPinResponseMessage: string | null;
  kioskApiAuthorization: string;
}

export interface UserStoreStatus {
  resetPinLoading: boolean;
}

export interface UserStoreErrors {
  authError: Error | null;
  resetPinError: Error | null;
}

export interface UserStore {
  data: UserStoreData;
  status: UserStoreStatus;
  errors: UserStoreErrors;
}

const EVENT_SOURCES = {
  LoginWithPin: 'Log in with PIN on kiosk',
};

export const AUTO_LOGOUT_TIMER_DURATION_IN_SECONDS = 7;

export const AUTO_LOGOUT_TIMER_DURATION_IN_MILLISECONDS =
  AUTO_LOGOUT_TIMER_DURATION_IN_SECONDS * 1000;

export const useUserStore = defineStore({
  id: 'user',
  state: (): UserStore => ({
    data: {
      activeUser: null,
      resetPinResponseMessage: null,
      kioskApiAuthorization: '',
    },
    status: {
      resetPinLoading: false,
    },
    errors: {
      authError: null,
      resetPinError: null,
    },
  }),
  getters: {
    isAuthenticated(state) {
      return () => state.data.activeUser !== null;
    },
    activeUserOrThrow(state) {
      const activeUser = state.data.activeUser;

      if (!activeUser) {
        throw new Error('No active user was found');
      }

      return activeUser;
    },
    getKioskApiAuthorization() {
      return (userId: number, pin: string) =>
        `Kiosk ${getKioskInstallationId()} ${window.btoa(pin)} ${userId}`;
    },
    isUserActive() {
      return () =>
        this.data.activeUser || router.currentRoute.value.name !== 'home';
    },
  },
  actions: {
    login(employeeId: number, pin: string): boolean {
      this.errors.authError = null;

      const analytics = useAnalyticsService();
      const employeeStore = useEmployeeStore();
      const employee = employeeStore.getByEmployeeId(employeeId);

      if (!employee) {
        this.errors.authError = Error(t('error-employee-not-found'));
        return false;
      }

      this.data.kioskApiAuthorization = this.getKioskApiAuthorization(
        employee.UserId,
        pin
      );

      const encryptedPin = Md5.hashStr(
        getKioskInstallationId() + pin + employee.UserId
      );

      if (employee.Pin === encryptedPin) {
        this.data.activeUser = employee;
        reloadAnalyticsService({
          employee,
        });
        analytics.track(
          { name: 'LOGIN_INTO_INSTALLATION_COMPLETED', screen: 'LOGIN' },
          { source: EVENT_SOURCES.LoginWithPin }
        );
      } else {
        this.errors.authError = Error(t('error-incorrect-pin'));
      }

      return !this.errors.authError;
    },
    logout() {
      this.data.activeUser = null;
      this.data.kioskApiAuthorization = '';
      resetAnalyticsService();
    },
    async resetPin(employeeId: number, mode: ResetPinMode) {
      this.status.resetPinLoading = true;
      let response;
      const error = Error(t('reset-pin-error'));

      try {
        response = (await resetPin(
          employeeId,
          mode
        )) as ResetPinResponsePayload;
      } catch {
        this.errors.resetPinError = error;
      }

      switch (response?.message) {
        case resetPinResponseErrorMessage:
          this.errors.resetPinError = error;
          break;
        case resetPinResponseEmailErrorMessage:
        case resetPinResponseMobileErrorMessage:
          this.errors.resetPinError = Error(response?.message);
          break;
        default:
          this.data.resetPinResponseMessage = t('reset-pin-message', {
            destination: response?.message.split(' ').pop(),
          });
          break;
      }

      this.status.resetPinLoading = false;
    },
    /* istanbul ignore next */
    restartResetPin() {
      this.data.resetPinResponseMessage = null;
      this.status.resetPinLoading = false;
      this.errors.resetPinError = null;
    },
  },
});
